import React from 'react';

import { DeleteIcon } from '@vlabs/icons';
import { useModal } from '@vlabs/uikit';
import type { IconButtonProps } from '@vlabs/uikit/components/controls/button/icon-button';
import { IconButton } from '@vlabs/uikit/components/controls/button/icon-button';
import { toast } from 'react-toastify';

import { DeleteRolesForm } from '@vlabs/monokit/entities/roles';
import type { DeleteRolesFormValues } from '@vlabs/monokit/entities/roles';
import { buildTestid } from '@vlabs/monokit/shared/lib/utils';

import { deleteRolesAndDetachUsers } from './deleteRolesAndDetachUsers';
import { useDeleteRoleTranslation } from './i18n';
import type { DeleteRoles } from './types';

interface DeleteRolesIconButtonProps extends DeleteRoles, Omit<IconButtonProps, 'onClick'> { }

const displayName = 'DeleteRolesIconButton';
const testid = buildTestid(displayName);

function DeleteRolesIconButton({
  roleIds,
  onDeleteRoles = undefined,
  'data-testid': dataTestId = undefined,
  ...props
}: DeleteRolesIconButtonProps): React.JSX.Element {
  const { t } = useDeleteRoleTranslation();
  const modal = useModal({ size: 'small' });

  const handleSubmit = async (values: DeleteRolesFormValues): Promise<void> => {
    try {
      await deleteRolesAndDetachUsers(values);
      toast.success(t('toast_success'));
      modal.close();
      onDeleteRoles?.();
    } catch {
      toast.error(t('toast_error'));
    }
  };

  return (
    <React.Fragment>
      {modal.wrap(
        <DeleteRolesForm
          defaultValues={{ roleIds }}
          onCancel={modal.close}
          onSubmit={handleSubmit}
        />,
      )}

      <IconButton
        icon={<DeleteIcon />}
        kind="negative"
        onClick={modal.open}
        shape="round"
        variant="flat"
        {...testid(dataTestId)}
        {...props}
      />
    </React.Fragment>
  );
}

DeleteRolesIconButton.displayName = displayName;

export { DeleteRolesIconButton };
